import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  applyMove,
  getVariant,
  initialBoard,
  type Board as BoardState,
  type Move,
  type VariantId,
} from "@fmjd/engine";
import { Board } from "../components/Board.js";
import { MoveList, type PlayedMove } from "../components/MoveList.js";
import { GameSocket, type ServerMessage } from "../gameSocket.js";
import { scoreline } from "../results.js";

/**
 * A live game.
 *
 * The server owns the clocks and the rules; this page displays them. The local
 * clock ticks only so the display is smooth between messages — every
 * authoritative value comes from the socket.
 *
 * Any position in the game can be replayed, by anyone, at any time — during
 * play too. The live board is never replaced by a replayed one silently: while
 * an earlier position is shown the board cannot be moved on, and a bar says
 * so, with a way straight back.
 */

interface Snapshot {
  publicId: string;
  variant: VariantId;
  board: BoardState;
  turn: "w" | "b";
  ply: number;
  legalMoves: Move[];
  lastMove: Move | null;
  clock: { whiteMs: number; blackMs: number };
  running: "w" | "b" | null;
  state: "pending" | "running" | "paused" | "finished";
  result: "white" | "black" | "draw" | null;
  resultReason: string | null;
  drawOfferedBy: "w" | "b" | null;
  whiteConnected: boolean;
  blackConnected: boolean;
  white?: { nickname: string; publicId: string };
  black?: { nickname: string; publicId: string };
  you?: "w" | "b" | null;
}

export function Play() {
  const { publicId } = useParams();
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [connected, setConnected] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const [partialPath, setPartialPath] = useState<number[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const socket = useRef<GameSocket | null>(null);
  /**
   * When the clocks on screen were last set from the server. The running clock
   * counts down from there (#191): without it the display stood still between
   * moves, while the real clock ran on.
   */
  const clockAt = useRef(Date.now());

  // --- replay ---------------------------------------------------------------
  const [moves, setMoves] = useState<PlayedMove[]>([]);
  const [initialMs, setInitialMs] = useState(0);
  /** The ply being viewed, or null to follow the game live. */
  const [viewPly, setViewPly] = useState<number | null>(null);
  const [autoplay, setAutoplay] = useState(false);
  const [flip, setFlip] = useState(false);
  /** Read inside socket handlers, which would otherwise see a stale list. */
  const movesLen = useRef(0);
  movesLen.current = moves.length;

  const loadMoves = useCallback(() => {
    if (!publicId) return;
    void fetch(`/api/games/${publicId}/moves`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        setMoves(d.moves);
        setInitialMs(d.initialMs);
      })
      .catch(() => {});
  }, [publicId]);

  /** Local tick, purely so the clock display moves between server messages. */
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 200);
    return () => clearInterval(t);
  }, []);

  const onMessage = useCallback(
    (msg: ServerMessage) => {
      const m = msg as Record<string, any>;
      switch (m.type) {
        case "state":
        case "started":
        case "finished":
        case "move":
        case "rejected":
          // Every one of these carries the whole position. `started`, `finished`
          // and `move` go to every watcher, so they carry no `you`: merging them
          // naively would drop the side we play and make the board unclickable.
          clockAt.current = Date.now();
          setSnap((prev) => ({ ...(prev ?? {}), ...m, you: m.you ?? prev?.you ?? null }) as Snapshot);
          setSelected(null);
          setPartialPath([]);
          if (m.type === "move") {
            setMoves((prev) => {
              if (m.ply !== prev.length + 1) return prev;
              const mv = m.move as Move;
              return [
                ...prev,
                {
                  ...mv,
                  ply: m.ply,
                  notation: `${mv.from}${mv.captures.length ? "x" : "-"}${mv.to}`,
                  whiteMs: Math.round(m.clock.whiteMs),
                  blackMs: Math.round(m.clock.blackMs),
                  thinkMs: null,
                },
              ];
            });
          } else if (typeof m.ply === "number" && m.ply > movesLen.current) {
            // Moves missed while disconnected: fetch the score again rather
            // than replay a board that is missing plies.
            loadMoves();
          }
          if (m.type === "rejected") setNotice(m.message ?? "That move was not accepted");
          break;
        case "refused":
          // A resign or draw action the server would not take, with why.
          setNotice(m.message ?? "That was not possible");
          break;
        case "draw_offered":
          setNotice(`${m.by === "w" ? "White" : "Black"} offers a draw`);
          setSnap((prev) => (prev ? { ...prev, drawOfferedBy: m.by } : prev));
          break;
        case "draw_declined":
          setNotice("Draw declined");
          setSnap((prev) => (prev ? { ...prev, drawOfferedBy: null } : prev));
          break;
      }
    },
    [loadMoves]
  );

  useEffect(() => {
    if (!publicId) return;
    loadMoves();
    void fetch(`/api/games/${publicId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        clockAt.current = Date.now();
        setSnap(d);
      })
      .catch(() => {});
    const s = new GameSocket(publicId, onMessage, setConnected);
    socket.current = s;
    // Leaving the page closes it for good: it must not reconnect behind us.
    return () => {
      s.close();
      if (socket.current === s) socket.current = null;
    };
  }, [publicId, onMessage, loadMoves]);

  // Every position in the game, rebuilt with the engine's own move function:
  // the same code that judged the moves legal draws the replay.
  const variantId = snap?.variant;
  const positions = useMemo(() => {
    if (!variantId) return [];
    let board = initialBoard(getVariant(variantId));
    const boards: BoardState[] = [board];
    for (const m of moves) {
      board = applyMove(board, m);
      boards.push(board);
    }
    return boards;
  }, [variantId, moves]);

  const latest = moves.length;
  const step = useCallback(
    (delta: number) =>
      setViewPly((prev) => {
        const next = Math.max(0, Math.min(latest, (prev ?? latest) + delta));
        return next >= latest ? null : next;
      }),
    [latest]
  );

  // Arrow keys step through the game; Home and End jump to either end.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      if (e.key === "ArrowLeft") step(-1);
      else if (e.key === "ArrowRight") step(1);
      else if (e.key === "Home") setViewPly(0);
      else if (e.key === "End") setViewPly(null);
      else return;
      e.preventDefault();
      setAutoplay(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step]);

  // Auto-play stops by itself on reaching the live position.
  useEffect(() => {
    if (!autoplay) return;
    if (viewPly === null) {
      setAutoplay(false);
      return;
    }
    const t = setTimeout(() => step(1), 900);
    return () => clearTimeout(t);
  }, [autoplay, viewPly, step]);

  if (!snap) return <p style={{ color: "#666" }}>Loading…</p>;
  // Captured after the guard so handlers below close over a non-null value:
  // `snap` itself is a `useState` binding and stays nullable to TypeScript.
  const game = snap;

  const variant = getVariant(snap.variant);
  const you = snap.you ?? null;
  /** An earlier position is on the board, not the current one. */
  const replaying = viewPly !== null && viewPly < positions.length - 1;
  const shownPly = replaying ? viewPly : latest;
  const myTurn = you !== null && snap.turn === you && snap.state === "running" && !replaying;
  const shownBoard = replaying ? (positions[viewPly] ?? snap.board) : snap.board;
  /** The move that led to the position on screen, when replaying. */
  const viewedMove = replaying ? moves[viewPly - 1] : undefined;
  const shownLastMove = replaying ? (viewedMove ?? null) : snap.lastMove;
  const shownClock = !replaying
    ? liveClock(snap, clockAt.current)
    : viewedMove
      ? { whiteMs: viewedMove.whiteMs, blackMs: viewedMove.blackMs }
      : { whiteMs: initialMs, blackMs: initialMs };
  const flipped = flip ? you !== "b" : you === "b";

  // Which squares may be clicked next, given the capture entered so far.
  const matching = snap.legalMoves.filter(
    (m) =>
      m.from === selected &&
      partialPath.every((sq, i) => m.path[i] === sq) &&
      m.path.length >= partialPath.length
  );
  const targets = [...new Set(matching.map((m) => m.path[partialPath.length]).filter(Boolean))] as number[];
  const origins = new Set(snap.legalMoves.map((m) => m.from));

  function onSquareClick(square: number) {
    if (!myTurn) return;

    if (selected === null) {
      if (origins.has(square)) setSelected(square);
      return;
    }

    // Clicking the origin cancels — unless it is a legal next hop, which is
    // the circular-capture case where a piece returns to where it started.
    if (!targets.includes(square)) {
      if (square === selected) {
        setSelected(null);
        setPartialPath([]);
      } else {
        setSelected(origins.has(square) ? square : null);
        setPartialPath([]);
      }
      return;
    }

    const path = [...partialPath, square];
    const still = matching.filter((m) => path.every((sq, i) => m.path[i] === sq));
    const complete = still.find((m) => m.path.length === path.length);
    const longer = still.filter((m) => m.path.length > path.length);

    if (complete && longer.length === 0) {
      socket.current?.send({ type: "move", from: complete.from, to: complete.to, path: complete.path });
      setSelected(null);
      setPartialPath([]);
      return;
    }
    setPartialPath(path);
  }

  function act(type: string) {
    socket.current?.send({ type });
  }

  return (
    <div style={{ display: "flex", gap: 28, flexWrap: "wrap", alignItems: "flex-start" }}>
      <div>
        <PlayerBar
          nickname={(flipped ? snap.white?.nickname : snap.black?.nickname) ?? (flipped ? "White" : "Black")}
          ms={flipped ? shownClock.whiteMs : shownClock.blackMs}
          running={!replaying && snap.running === (flipped ? "w" : "b") && snap.state === "running"}
          connected={flipped ? snap.whiteConnected : snap.blackConnected}
          side="b"
        />
        <Board
          variant={variant}
          board={shownBoard}
          flipped={flipped}
          selected={replaying ? null : selected}
          targets={replaying ? [] : targets}
          pathSquares={replaying || selected === null ? [] : [selected, ...partialPath]}
          lastMove={shownLastMove}
          onSquareClick={onSquareClick}
          size={520}
        />
        <PlayerBar
          nickname={(flipped ? snap.black?.nickname : snap.white?.nickname) ?? (flipped ? "Black" : "White")}
          ms={flipped ? shownClock.blackMs : shownClock.whiteMs}
          running={!replaying && snap.running === (flipped ? "b" : "w") && snap.state === "running"}
          connected={flipped ? snap.blackConnected : snap.whiteConnected}
          side="w"
        />
        <div style={{ display: "flex", gap: 6, marginTop: 10, alignItems: "center" }}>
          <button title="Start (Home)" onClick={() => { setAutoplay(false); setViewPly(0); }} style={navBtn} disabled={shownPly === 0}>⏮</button>
          <button title="Back (←)" onClick={() => { setAutoplay(false); step(-1); }} style={navBtn} disabled={shownPly === 0}>◀</button>
          <button
            title={autoplay ? "Pause" : "Play through the game"}
            onClick={() => {
              if (autoplay) return setAutoplay(false);
              if (!replaying) setViewPly(0);
              setAutoplay(true);
            }}
            style={navBtn}
            disabled={latest === 0}
          >
            {autoplay ? "⏸" : "▶"}
          </button>
          <button title="Forward (→)" onClick={() => { setAutoplay(false); step(1); }} style={navBtn} disabled={!replaying}>▶|</button>
          <button title="Current position (End)" onClick={() => { setAutoplay(false); setViewPly(null); }} style={navBtn} disabled={!replaying}>⏭</button>
          <span style={{ fontSize: 13, color: "#666", marginLeft: 6 }}>
            {shownPly === 0 ? "Start" : `Move ${Math.ceil(shownPly / 2)}${shownPly % 2 ? "" : "…"}`}
          </span>
          <button onClick={() => setFlip((f) => !f)} style={{ ...smallBtn, marginLeft: "auto" }}>
            Flip board
          </button>
        </div>
      </div>

      <div style={{ minWidth: 240, flex: 1 }}>
        {replaying && (
          <div
            style={{
              ...box,
              background: "#e3f2fd",
              borderColor: "#90caf9",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span>
              Viewing move {Math.ceil(shownPly / 2)}
              {snap.state === "finished" && (
                <span style={{ color: "#555" }}>
                  {" "}· final result {scoreline(snap.result)}
                </span>
              )}
              {snap.state !== "finished" && myTurnLive(snap, you) && (
                <strong style={{ color: "#c62828" }}> — your clock is running</strong>
              )}
            </span>
            <button onClick={() => { setAutoplay(false); setViewPly(null); }} style={btn}>
              {snap.state === "finished" ? "Final position" : "Back to live"}
            </button>
          </div>
        )}

        {!connected && (
          <div style={{ ...box, background: "#fff8e1", borderColor: "#ffe082" }}>
            Reconnecting… your clock is still running.
          </div>
        )}

        {snap.state === "finished" ? (
          <div style={{ ...box, background: "#e8f5e9", borderColor: "#a5d6a7" }}>
            <strong style={{ fontSize: 16 }}>
              {snap.result === "draw"
                ? "Draw"
                : snap.result === "white"
                  ? "White wins"
                  : snap.result === "black"
                    ? "Black wins"
                    : "Double forfeit — 0–0"}
            </strong>
            <div style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
              {snap.result ? describeReason(snap.resultReason) : "Neither player appeared (FMJD Annex 3 §5.8)"}
            </div>
          </div>
        ) : (
          <div style={box}>
            <strong>{snap.turn === "w" ? "White" : "Black"} to move</strong>
            {myTurn && <div style={{ fontSize: 13, color: "#00695c" }}>Your move</div>}
            {partialPath.length > 0 && (
              <div style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
                Capturing {selected}
                {partialPath.map((s) => `×${s}`).join("")} — click the next square
              </div>
            )}
          </div>
        )}

        {notice && (
          <div style={{ ...box, background: "#f5f5f5" }}>
            {notice}
            <button onClick={() => setNotice(null)} style={{ ...smallBtn, marginLeft: 8 }}>
              ok
            </button>
          </div>
        )}

        <div style={{ margin: "12px 0" }}>
          <MoveList
            moves={moves}
            current={shownPly}
            result={
              snap.state === "finished"
                ? {
                    score: scoreline(snap.result),
                    reason: snap.result
                      ? describeReason(snap.resultReason)
                      : "Neither player appeared (FMJD Annex 3 §5.8)",
                  }
                : null
            }
            onSelect={(ply) => {
              setAutoplay(false);
              setViewPly(ply >= latest ? null : ply);
            }}
          />
        </div>

        {you && snap.state === "running" && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
            {snap.drawOfferedBy && snap.drawOfferedBy !== you ? (
              <>
                <button onClick={() => act("draw_accept")} style={btn}>
                  Accept draw
                </button>
                <button onClick={() => act("draw_decline")} style={smallBtn}>
                  Decline
                </button>
              </>
            ) : (
              <button
                onClick={() => act("draw_offer")}
                disabled={snap.drawOfferedBy === you}
                style={smallBtn}
              >
                {snap.drawOfferedBy === you ? "Draw offered" : "Offer draw"}
              </button>
            )}
            <button
              onClick={() => {
                if (confirm("Resign this game?")) act("resign");
              }}
              style={{ ...smallBtn, borderColor: "#ef9a9a", color: "#c62828" }}
            >
              Resign
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Whether it is this player's turn in the live game, whatever is on screen. */
function myTurnLive(snap: Snapshot, you: "w" | "b" | null): boolean {
  return you !== null && snap.turn === you && snap.state === "running";
}

/**
 * The clocks as they stand now: the running side counts down from the moment
 * the server's value arrived. Display only; the server keeps the real clock and
 * corrects this on every message.
 */
function liveClock(snap: Snapshot, at: number): { whiteMs: number; blackMs: number } {
  const c = snap.clock;
  if (snap.state !== "running" || !snap.running) return c;
  const elapsed = Math.max(0, Date.now() - at);
  return snap.running === "w"
    ? { whiteMs: Math.max(0, c.whiteMs - elapsed), blackMs: c.blackMs }
    : { whiteMs: c.whiteMs, blackMs: Math.max(0, c.blackMs - elapsed) };
}

function PlayerBar({
  nickname,
  ms,
  running,
  connected,
  side,
}: {
  nickname: string;
  ms: number;
  running: boolean;
  connected: boolean;
  side: "w" | "b";
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "8px 12px",
        background: running ? "#e0f2f1" : "#fafafa",
        border: "1px solid #e0e0e0",
        borderRadius: 6,
        margin: side === "b" ? "0 0 8px" : "8px 0 0",
      }}
    >
      <span style={{ fontWeight: 600, fontSize: 14 }}>
        {nickname}
        {!connected && (
          <span style={{ marginLeft: 8, fontSize: 11, color: "#e65100", fontWeight: 400 }}>
            not connected
          </span>
        )}
      </span>
      <span
        style={{
          fontFamily: "ui-monospace, monospace",
          fontSize: 20,
          fontWeight: 700,
          color: ms < 30_000 ? "#c62828" : "#222",
        }}
      >
        {formatClock(ms)}
      </span>
    </div>
  );
}

function formatClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  // Under ten seconds, show tenths: the last seconds of a blitz game are
  // where the display matters most.
  if (ms < 10_000) return `${(Math.max(0, ms) / 1000).toFixed(1)}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function describeReason(reason: string | null): string {
  switch (reason) {
    case "timeout":
      return "Lost on time";
    case "resign":
      return "By resignation";
    case "agreement":
      return "By agreement";
    case "blocked":
      return "No legal move (FMJD 7.2.3)";
    case "no-pieces":
      return "No pieces remaining (FMJD 7.2.2)";
    case "repetition":
      return "Threefold repetition (FMJD 6.1)";
    case "no-progress":
      return "25 king moves without a capture (FMJD 6.2)";
    case "endgame-3v1":
      return "Three against one, 16 moves (FMJD 6.3)";
    case "endgame-2v1":
      return "Two against one, 5 moves (FMJD 6.4)";
    case "arbiter":
      return "Decided by the arbiter";
    case "forfeit":
      return "Forfeit: a player did not appear";
    default:
      return reason ?? "";
  }
}

const box: React.CSSProperties = {
  padding: "10px 12px",
  background: "#f5f5f5",
  border: "1px solid #e0e0e0",
  borderRadius: 6,
  marginBottom: 10,
  fontSize: 14,
};

const btn: React.CSSProperties = {
  padding: "8px 16px",
  fontSize: 14,
  fontWeight: 600,
  color: "#fff",
  background: "#00695c",
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
};

const navBtn: React.CSSProperties = {
  width: 38,
  height: 32,
  fontSize: 14,
  border: "1px solid #ccc",
  borderRadius: 6,
  background: "#fff",
  cursor: "pointer",
};

const smallBtn: React.CSSProperties = {
  padding: "6px 12px",
  fontSize: 13,
  border: "1px solid #ccc",
  borderRadius: 6,
  background: "#fff",
  cursor: "pointer",
};
